// The three looks as one row of icon keys, so every choice is in view and
// the current one is lit. The name of a look is the tooltip of its key.

import { el } from '../lib/dom';
import { svg } from '../lib/svg';
import { THEME_MODES, type ThemeMode, themeMode } from '../lib/theme';
import { createSideMark } from './SideMark';

const THEME_LABELS: Record<ThemeMode, string> = {
  dark: 'Night',
  light: 'Day',
  mono: 'Mono',
};

const ICON_BOX = 24;

/** A ring with its right half solid: one bit, black and white. */
const MONO_PATH = 'M12 3 A9 9 0 1 0 12 21 A9 9 0 1 0 12 3 Z M12 5 V19 A7 7 0 0 1 12 5 Z';

/** Night and Day wear the marks of the two sides: the crescent and the sun. */
const THEME_ICONS: Record<ThemeMode, () => SVGSVGElement> = {
  dark: () => createSideMark('violet'),
  light: () => createSideMark('gold'),
  mono: () =>
    svg('svg', { viewBox: `0 0 ${ICON_BOX} ${ICON_BOX}`, 'aria-hidden': 'true' }, [
      svg('path', { d: MONO_PATH, 'fill-rule': 'evenodd' }),
    ]),
};

export const createThemePicker = (): HTMLElement => {
  const keys = new Map(
    THEME_MODES.map((mode) => [
      mode,
      el(
        'button',
        {
          className: 'picker__key',
          type: 'button',
          ariaLabel: THEME_LABELS[mode],
          dataset: { tip: THEME_LABELS[mode] },
          onclick: () => themeMode.set(mode),
        },
        [THEME_ICONS[mode]()],
      ),
    ]),
  );

  const draw = (current: ThemeMode): void => {
    for (const [mode, key] of keys) key.ariaPressed = String(mode === current);
  };

  draw(themeMode.get());
  themeMode.subscribe(draw);
  return el('div', { className: 'picker', role: 'group', ariaLabel: 'Look' }, [...keys.values()]);
};
